import { Link } from "@frontend/lib/link";
import { AccountSwitcher } from "@frontend/components/account-switcher";
import { NewCollabButton } from "@frontend/components/new-collab-button";
import { ProfileMenu } from "@frontend/components/profile-menu";
import { WorkspaceNav } from "@frontend/components/workspace-nav";
import { BrandLogo } from "@frontend/components/brand-logo";
import { LinkButton } from "@frontend/components/ui";
import { clientApi } from "@frontend/lib/api";
import { creatorAvatarUrl } from "@frontend/lib/avatar";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { collabNeedsYou } from "@frontend/lib/collab-tabs";
import { useAsync } from "@frontend/lib/use-async";
import type { Viewer } from "@creators/backend/lib/auth";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";

/**
 * Who is signed in, which is not the same as the account in view. The profile menu
 * names the person; the switcher beside it names the account they are working in.
 */
function displayName(viewer: Viewer): string {
  if (viewer.isAdmin) return "Admin";
  const owned = viewer.accounts.find((account) => account.own);
  return owned?.displayName ?? viewer.brand?.name ?? viewer.phone ?? "Account";
}

/**
 * The photo belongs to the person's *own* creator account, not to whichever
 * account they happen to be standing in — the menu names them, not the account.
 */
function ownAvatar(viewer: Viewer): string | null {
  if (viewer.isAdmin) return null;
  const owned = viewer.accounts.find((account) => account.own);
  return owned ? creatorAvatarUrl(owned.creatorId, owned.avatarKey) : null;
}

function ownPagePath(viewer: Viewer): string | null {
  if (viewer.isAdmin) return null;
  const owned = viewer.accounts.find((account) => account.own);
  return owned?.profilePath ?? null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export function TopNav({
  viewer,
  links,
}: {
  viewer: Viewer | null;
  /** Marketing sections, passed only by the public site. */
  links?: { label: string; href: string }[];
}) {
  const name = viewer ? displayName(viewer) : "";
  const inWorkspace = Boolean(viewer && !viewer.isAdmin);
  const cacheKey = collabsCacheKey(viewer?.sessionId);

  const inbox = useAsync(
    async () => {
      if (!viewer || viewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs");
      return collabs;
    },
    [viewer?.sessionId, viewer?.isAdmin, viewer?.creator?.id],
    { cacheKey },
  );

  const actionCount = (inbox.data ?? []).filter(collabNeedsYou).length;

  return (
    <header className="glass sticky top-0 z-20 pt-[env(safe-area-inset-top)]">
      <div className="relative mx-auto grid w-full max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2 justify-self-start sm:gap-3">
          <Link
            href={viewer ? "/overview" : "/"}
            className="flex shrink-0 items-center"
            aria-label="SendCollab"
          >
            <BrandLogo />
          </Link>

          {inWorkspace && viewer ? (
            <AccountSwitcher
              accounts={viewer.accounts}
              activeCreatorId={viewer.creator?.id ?? null}
              personalLabel={viewer.brand?.name ?? viewer.phone ?? "Personal"}
            />
          ) : null}
        </div>

        <div className="justify-self-center">
          {inWorkspace && viewer ? (
            <WorkspaceNav showAccess={Boolean(viewer.creator)} actionCount={actionCount} />
          ) : links ? (
            <nav aria-label="Page sections" className="hidden items-center gap-0.5 md:flex">
              {links.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg px-3 py-2 text-sm text-ink transition hover:bg-ink/5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          ) : null}
        </div>

        {viewer ? (
          <div className="flex shrink-0 items-center justify-end gap-1.5 justify-self-end sm:gap-3">
            {!viewer.isAdmin && viewer.brand ? <NewCollabButton /> : null}
            <ProfileMenu
              name={name}
              phone={viewer.phone}
              initials={initials(name)}
              avatarUrl={ownAvatar(viewer)}
              pagePath={ownPagePath(viewer)}
            />
          </div>
        ) : (
          <nav className="flex shrink-0 items-center justify-end gap-1 justify-self-end text-sm">
            <Link
              href="/login"
              className="rounded-lg px-3 py-2.5 text-ink transition hover:bg-ink/5"
            >
              Sign in
            </Link>
            <LinkButton href="/signup" size="sm">
              Sign up
            </LinkButton>
          </nav>
        )}
      </div>
    </header>
  );
}
